import React from 'react';
import { callerParameters, patientParameters } from './xomics-webserver-client';
import './webserver-shared.css';

// The form for one pipeline, rendered from the contract the catalogue returns:
// the patient facts it needs (requires_patient_fields) and every parameter its
// pipeline.json leaves to the caller, under the pipeline's own names. A value
// left empty is not sent, so the pipeline's declared default applies.

const DEFAULT_CLASSES = {
  group: 'WSX-field',
  label: 'WSX-field-label',
  input: 'WSX-field-input',
  select: 'WSX-field-input',
  description: 'WSX-field-description',
  required: 'WSX-field-required',
};

function factLabel(field) {
  const text = String(field || '').replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function hasDefault(parameter) {
  return parameter.default !== undefined && parameter.default !== null && parameter.default !== '';
}

function Field({ parameter, label, required, value, onChange, disabled, classes, idPrefix }) {
  const id = `${idPrefix}-${parameter.name}`;
  let control;
  if (Array.isArray(parameter.enum) && parameter.enum.length > 0) {
    control = (
      <select
        id={id}
        className={classes.select}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      >
        <option value="">
          {hasDefault(parameter) ? `Pipeline default (${parameter.default})` : 'Select...'}
        </option>
        {parameter.enum.map((option) => (
          <option key={String(option)} value={String(option)}>{String(option)}</option>
        ))}
      </select>
    );
  } else if (Array.isArray(parameter.range) && parameter.range.length === 2) {
    control = (
      <input
        id={id}
        type="number"
        className={classes.input}
        min={parameter.range[0]}
        max={parameter.range[1]}
        step="any"
        value={value ?? ''}
        placeholder={hasDefault(parameter) ? String(parameter.default) : `${parameter.range[0]}-${parameter.range[1]}`}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
    );
  } else {
    control = (
      <input
        id={id}
        type="text"
        className={classes.input}
        value={value ?? ''}
        placeholder={hasDefault(parameter) ? String(parameter.default) : ''}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
    );
  }
  const hints = [];
  if (parameter.description) hints.push(parameter.description);
  if (Array.isArray(parameter.range)) hints.push(`Allowed: ${parameter.range[0]} to ${parameter.range[1]}`);
  if (hasDefault(parameter)) hints.push(`Default: ${parameter.default}`);
  return (
    <div className={classes.group} data-parameter={parameter.name}>
      <label className={classes.label} htmlFor={id}>
        {label}
        {required && <span className={classes.required}> *</span>}
      </label>
      {control}
      {hints.length > 0 && <p className={classes.description}>{hints.join(' · ')}</p>}
    </div>
  );
}

export default function PipelineContractFields({
  pipeline,
  values,
  onChange,
  disabled = false,
  classes = {},
  idPrefix = 'wsx',
}) {
  const merged = { ...DEFAULT_CLASSES, ...classes };
  const facts = patientParameters(pipeline);
  const parameters = callerParameters(pipeline);
  const setFact = (field, value) => onChange({
    ...values,
    facts: { ...(values?.facts || {}), [field]: value },
  });
  const setParameter = (name, value) => onChange({
    ...values,
    parameters: { ...(values?.parameters || {}), [name]: value },
  });

  return (
    <>
      {facts.map((parameter) => (
        <Field
          key={`fact-${parameter.patient_field}`}
          parameter={parameter}
          label={factLabel(parameter.patient_field)}
          required
          value={values?.facts?.[parameter.patient_field]}
          onChange={(value) => setFact(parameter.patient_field, value)}
          disabled={disabled}
          classes={merged}
          idPrefix={idPrefix}
        />
      ))}
      {parameters.map((parameter) => (
        <Field
          key={`parameter-${parameter.name}`}
          parameter={parameter}
          label={parameter.title || parameter.name}
          required={Boolean(parameter.required) && !hasDefault(parameter)}
          value={values?.parameters?.[parameter.name]}
          onChange={(value) => setParameter(parameter.name, value)}
          disabled={disabled}
          classes={merged}
          idPrefix={idPrefix}
        />
      ))}
    </>
  );
}
